import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Video, 
  Calendar, 
  Clock, 
  User, 
  ArrowLeft,
  Loader2,
  AlertCircle,
  Phone,
  MessageCircle
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { VideoCall } from "@/components/VideoCall";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";

export default function VideoCallPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inCall, setInCall] = useState(false);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const fetchBookingDetails = async () => {
    try {
      // Try to get from user's bookings first
      const response = await axios.get(`${API}/bookings/me`);
      let foundBooking = response.data.find(b => b.id === bookingId);
      
      // If not found in user bookings, try expert bookings
      if (!foundBooking) {
        try {
          const expertResponse = await axios.get(`${API}/expert/bookings`);
          if (expertResponse.data?.upcoming) {
            foundBooking = expertResponse.data.upcoming.find(b => b.id === bookingId);
          }
        } catch (e) {
          // Not an expert or no expert bookings, that's ok
        }
      }
      
      if (!foundBooking) {
        setError("Booking not found or you don't have access to this session");
      } else {
        setBooking(foundBooking);
      }
    } catch (err) {
      console.error("Error fetching booking:", err);
      setError("Failed to load booking details");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCall = () => {
    setInCall(true);
  };

  const handleLeaveCall = () => {
    setInCall(false);
    toast.success("You left the video call");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center pt-16">
          <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-obsidian flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center pt-16 px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2 text-center">{error}</h2>
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian flex flex-col">
      {/* Fixed Header */}
      <Navbar />
      
      {/* Main Content - Scrollable area between header and footer */}
      <main className="flex-1 pt-16 sm:pt-20 pb-16 overflow-auto">
        {inCall ? (
          /* Video Call Interface - Contained within app bounds */
          <div className="h-full flex flex-col">
            {/* Session Info Bar */}
            <div className="bg-charcoal border-b border-slate-800 px-4 py-3 shrink-0">
              <div className="max-w-6xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gold-500/20 rounded-full flex items-center justify-center">
                    <Video className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500" />
                  </div>
                  <div>
                    <h1 className="text-sm sm:text-base font-semibold text-white">Live Session</h1>
                    <p className="text-xs sm:text-sm text-slate-400">with {booking?.expert_name}</p>
                  </div>
                </div>
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-slate-400">{booking?.scheduled_date}</p>
                  <p className="text-xs text-gold-500">{booking?.scheduled_time}</p>
                </div>
              </div>
            </div>
            
            {/* Video Call Container - Takes remaining space */}
            <div className="flex-1 p-2 sm:p-4 min-h-0">
              <VideoCall 
                bookingId={bookingId} 
                onLeave={handleLeaveCall}
              />
            </div>
          </div>
        ) : (
          /* Pre-Call Lobby */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto px-4 py-6 sm:py-8"
          >
            {/* Back Button */}
            <Button 
              variant="ghost" 
              onClick={() => navigate("/dashboard")}
              className="mb-4 sm:mb-6 text-slate-400 hover:text-white -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>

            <div className="bg-charcoal border border-slate-800 rounded-lg p-5 sm:p-8">
              <div className="text-center mb-6 sm:mb-8">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Video className="w-8 h-8 sm:w-10 sm:h-10 text-gold-500" />
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-white mb-2">Ready to Join?</h1>
                <p className="text-sm sm:text-base text-slate-400">Your video session with {booking?.expert_name}</p>
              </div>

              {/* Session Details */}
              <div className="bg-slate-800/50 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Session Details</h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500 shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-slate-400">Expert</p>
                      <p className="text-sm sm:text-base text-white">{booking?.expert_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500 shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-slate-400">Date</p>
                      <p className="text-sm sm:text-base text-white">{booking?.scheduled_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500 shrink-0" />
                    <div>
                      <p className="text-xs sm:text-sm text-slate-400">Time</p>
                      <p className="text-sm sm:text-base text-white">{booking?.scheduled_time}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6 sm:mb-8">
                <h4 className="text-blue-400 font-medium mb-2 text-sm sm:text-base">Before you join:</h4>
                <ul className="text-slate-300 text-xs sm:text-sm space-y-1">
                  <li>• Make sure your camera and microphone are working</li>
                  <li>• Find a quiet, well-lit space</li>
                  <li>• Close other applications using your camera</li>
                  <li>• Have a stable internet connection</li>
                </ul>
              </div>

              {/* Join Button */}
              <Button 
                onClick={handleJoinCall}
                className="w-full bg-gold-500 hover:bg-gold-400 text-black py-5 sm:py-6 text-base sm:text-lg"
                data-testid="join-call-btn"
              >
                <Video className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Join Video Call
              </Button>

              {/* Alternative: Open in new tab */}
              {booking?.meeting_link && (
                <Button 
                  onClick={() => window.open(booking.meeting_link, '_blank')}
                  variant="outline"
                  className="w-full mt-3 border-slate-600 text-slate-300 hover:text-white text-sm sm:text-base"
                >
                  Open in New Tab Instead
                </Button>
              )}

              {/* Status Badge */}
              <div className="mt-4 text-center">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs sm:text-sm ${
                  booking?.status === "confirmed" 
                    ? "bg-green-500/20 text-green-500" 
                    : "bg-yellow-500/20 text-yellow-500"
                }`}>
                  {booking?.status === "confirmed" ? "Session Confirmed" : "Pending Confirmation"}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </main>

      {/* Fixed Footer - Always visible */}
      <footer className="fixed bottom-0 left-0 right-0 bg-charcoal border-t border-slate-800 py-2 sm:py-3 px-4 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-gold-500 font-display text-sm sm:text-base">
            ExpertBase
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <a 
              href="tel:+971558945236" 
              className="flex items-center gap-1 text-slate-400 hover:text-gold-500 text-xs sm:text-sm"
            >
              <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Support</span>
            </a>
            <a 
              href="https://wa.me/971558945236"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-slate-400 hover:text-green-500 text-xs sm:text-sm"
            >
              <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
